/**
 * DisclaimerFooter — rendered on every page (authenticated and public).
 * Australian-law compliant general-information disclaimer.
 */
import React, { useState } from 'react';

const CURRENT_YEAR = new Date().getFullYear();

export default function DisclaimerFooter() {
  const [expanded, setExpanded] = useState(false);

  return (
    <footer style={{
      borderTop: '1px solid var(--hairline, #e8e4d9)',
      background: 'var(--paper, #f5f1e8)',
      padding: expanded ? '14px 16px 18px' : '12px 16px',
      marginTop: 'auto',
      fontSize: 11,
      color: 'var(--ink-4, #9a9184)',
      lineHeight: 1.6,
      '@media (min-width: 768px)': { padding: expanded ? '14px 32px 18px' : '10px 32px' },
    }}>
      {/* Collapsed strip — always visible */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 10, color: 'var(--ink-3, #6b6560)' }}>
          General Information Only
        </span>
        <span style={{ display: 'block' }}>
          PropAtlas provides general property information and scenario modelling tools only — not financial, tax, legal, lending or investment advice.
          {' '}
          <button
            onClick={() => setExpanded(e => !e)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--forest, #1f3a2e)', fontWeight: 600, fontSize: 11, padding: 0, textDecoration: 'underline' }}
          >
            {expanded ? 'Show less' : 'Full disclaimer ↓'}
          </button>
        </span>
        <span style={{ fontSize: 10 }}>© {CURRENT_YEAR} PropAtlas Pty Ltd</span>
      </div>

      {/* Expanded full disclaimer */}
      {expanded && (
        <div style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid var(--hairline, #e8e4d9)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <p style={{ margin: 0 }}>
            <strong>General information only.</strong>{' '}
            All content, tools, calculators, projections, scenario models, market data, news summaries and commentary
            published on or accessible through the PropAtlas platform (collectively, "the Platform") are provided for
            general information and illustrative purposes only. Nothing on the Platform constitutes, or should be taken
            to constitute, financial product advice, tax advice, legal advice, credit advice, lending advice, investment
            advice, accounting advice or any other form of professional advice within the meaning of the{' '}
            <em>Corporations Act 2001</em> (Cth), the <em>Tax Agent Services Act 2009</em> (Cth), the{' '}
            <em>National Consumer Credit Protection Act 2009</em> (Cth) or any other applicable Australian law.
          </p>

          <p style={{ margin: 0 }}>
            <strong>No recommendation.</strong>{' '}
            Information on the Platform is not a recommendation, solicitation or offer to buy, sell or hold any
            property, financial product or security; to borrow, refinance or restructure any loan or credit facility;
            to invest or disinvest in any asset; to claim any tax deduction or offset; to lodge or amend any tax
            return; or to take or refrain from taking any other action. Any property valuations, rental yield
            estimates, capital growth projections, land tax estimates, negative gearing calculations, CGT estimates,
            ROI projections, scenario outputs and cash-flow models are illustrative only, based on the inputs you
            provide, and may not reflect your actual outcomes. Past performance is not an indicator of future results.
          </p>

          <p style={{ margin: 0 }}>
            <strong>Assumptions and limitations.</strong>{' '}
            Calculator outputs depend entirely on the accuracy and completeness of the information you enter. PropAtlas
            makes no representation that any rate, bracket, threshold or formula used in the Platform's tools is
            current, accurate or applicable to your circumstances. Tax rates, land tax thresholds, CGT rules, lending
            criteria and government policy change frequently and may differ from those used in the Platform's models.
            The Platform does not take into account your personal financial situation, investment objectives, risk
            profile, family circumstances, residency status or any other factor relevant to your individual position.
          </p>

          <p style={{ margin: 0 }}>
            <strong>Seek professional advice.</strong>{' '}
            Before making any decision in connection with property acquisition, disposal, financing, development,
            taxation, superannuation (including SMSF), estate planning or any other financial matter, you should
            obtain independent advice from a qualified professional licensed or registered for that purpose,
            including (as applicable) an Australian Financial Services (AFS) licensee or authorised representative,
            a registered tax agent or BAS agent, a lawyer or conveyancer, a licensed mortgage broker or credit
            representative, or a qualified property valuer.
          </p>

          <p style={{ margin: 0 }}>
            <strong>Australian Consumer Law.</strong>{' '}
            Nothing in this disclaimer limits, excludes or modifies any consumer guarantee, right or remedy
            conferred on you under the <em>Australian Consumer Law</em> (Schedule 2 to the{' '}
            <em>Competition and Consumer Act 2010</em> (Cth)) or any other law that cannot lawfully be excluded
            or restricted.
          </p>

          <p style={{ margin: 0, color: 'var(--ink-4, #9a9184)' }}>
            © {CURRENT_YEAR} PropAtlas Pty Ltd. ABN information is collected solely for identity verification and
            is not published to third parties. PropAtlas is not a registered managed investment scheme, AFS licensee,
            registered tax agent or credit licensee. This disclaimer was last updated May 2025.
          </p>
        </div>
      )}
    </footer>
  );
}
